import React from "react";
import { ScreenInterface, ApiHelper, ToastHelper } from "../../helpers";
import { Row, Col, FormControl, FormGroup, FormLabel, Button } from "react-bootstrap"
import { Timezones } from "./Timezones"
import { ManualPlaylists } from "./ManualPlaylists"
import { InputBox, Loading, PreviewModal, } from "../../components"
import { Navigate } from "react-router-dom";

interface Props {
  screen: ScreenInterface,
  refreshSidebar: () => void
  onUpdate: (screen: ScreenInterface) => void
  showPair: () => void
}

export const ScreenEdit: React.FC<Props> = props => {

  const [screen, setScreen] = React.useState<ScreenInterface>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [redirect, setRedirect] = React.useState("");
  const [manualCommand, setManualCommand] = React.useState<{ action: string, parameter: string }>(null);
  const [showPreview, setShowPreview] = React.useState(false);

  const handleChange = (e: any) => {
    let s: ScreenInterface = { ...screen };
    let val = e.currentTarget.value;
    switch (e.currentTarget.id) {
      case "name": s.name = val; break;
      case "mode": s.screenMode = val; break;
      case "timezone": s.timezone = val; break;
      case "rotation": s.rotation = parseInt(val, 0); break;
    }
    setScreen(s);
    props.onUpdate(s);
  }

  const validate = () => {
    if (screen.name === "") {
      ToastHelper.error("Please enter a screen name.");
      return false;
    }
    return true;
  }

  const handleSave = () => {
    if (validate()) {
      setIsSubmitting(true);
      screen.name = screen.name.trim();
      ApiHelper.post<ScreenInterface[]>("/screens", [screen])
        .then((data: ScreenInterface[]) => {
          if (screen.screenMode === "slideshow" || screen.screenMode === "both") setManualCommand({ action: "save", parameter: data[0].id.toString() });
          else manualSaved();
        })
        .catch((error) => {
          console.error("Error saving screen:", error);
          ToastHelper.error("Failed to save screen. Please try again.");
          setIsSubmitting(false);
        });
    }
  }

  const getDeleteFunction = () => (screen?.id > 0) ? handleDelete : undefined

  const handleDelete = () => {
    if (window.confirm("Are you sure you wish to permanently delete this screen?")) {
      ApiHelper.delete("/screens/" + screen.id).then(() => {
        props.refreshSidebar();
        setRedirect("/screens");
      });
    }
  }

  const manualSaved = () => {
    setIsSubmitting(false);
    props.refreshSidebar();
    if (props.screen?.id === 0) setRedirect("/screens");
    else ToastHelper.success("Changes saved successfully");
  }

  const getManual = () => {
    if ((screen.screenMode === "slideshow" || screen.screenMode === "both") && screen.id > 0) {
      return <ManualPlaylists screen={screen} pendingCommand={manualCommand} saveCallback={manualSaved} />
    } else return <></>
  }

  const getPreviewButton = () => {
    const result: React.ReactElement[] = [];
    if (screen.id > 0) result.push(<><Button data-testid="pair-tv-button" variant="success" size="sm" onClick={(e) => { e.preventDefault(); props.showPair() }}>Pair TV</Button> </>);
    if (screen?.id) result.push(<Button data-testid="screen-preview-button" variant="info" size="sm" onClick={(e) => { e.preventDefault(); setShowPreview(!showPreview) }}>Preview</Button>);
    return result;
  }

  const getPreviewModal = () => {
    if (showPreview) return <PreviewModal shape={(screen?.rotation === 90 || screen.rotation === 270) ? "9:16" : "16:9"} screenId={screen?.id} onClose={() => { setShowPreview(false) }} />
  }

  React.useEffect(() => { setScreen(props.screen) }, [props.screen]);

  if (redirect !== "") return <Navigate to={redirect} replace />
  else if (!screen) return <Loading />
  else return (
    <>
      <InputBox headerIcon="fas fa-tv" headerText="Edit Screen" saveFunction={handleSave} isSubmitting={isSubmitting} deleteFunction={getDeleteFunction()} headerActionContent={getPreviewButton()} >
        <Row>
          <Col>
            <FormGroup className="mb-3">
              <FormLabel htmlFor="name">Name</FormLabel>
              <FormControl data-testid="screen-name-input" name="name" id="name" value={screen.name} onChange={handleChange} />
            </FormGroup>
          </Col>
          <Col>
            <FormGroup className="mb-3">
              <FormLabel htmlFor="mode">Mode</FormLabel>
              <FormControl data-testid="screen-mode-select" as="select" name="mode" id="mode" value={screen.screenMode} onChange={handleChange} >
                <option value="digitalsignage">Digital Signage (Auto advance)</option>
                <option value="slideshow">Slideshow (Manual advance)</option>
                <option value="both">Both</option>
              </FormControl>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col>
            <FormGroup className="mb-3">
              <FormLabel htmlFor="timezone">Timezone</FormLabel>
              <FormControl data-testid="screen-timezone-select" as="select" name="timezone" id="timezone" value={screen.timezone} onChange={handleChange} >
                <Timezones />
              </FormControl>
            </FormGroup>
          </Col>
          <Col>
            <FormGroup className="mb-3">
              <FormLabel htmlFor="rotation">Rotation</FormLabel>
              <FormControl data-testid="screen-rotation-select" as="select" name="rotation" id="rotation" value={screen.rotation} onChange={handleChange}>
                <option value="0">0 - Horizontal</option>
                <option value="90">90 - Vertical (rotate right)</option>
                <option value="180">180 - Horizontal (upside down)</option>
                <option value="270">270 - Vertical (rotate left)</option>
              </FormControl>
            </FormGroup>
          </Col>
        </Row>
        {getManual()}
      </InputBox >
      {getPreviewModal()}
    </>

  );

}